import { GridDataResult } from '@progress/kendo-angular-grid';
import { Outage, Position, PositionBase } from '../planner.component';
import { VesselSchedule } from '@pmis3/vessel-activity-core';
import { ComponentRef, Renderer2, Type, ViewContainerRef } from '@angular/core';
import { TimelineDate } from '@pmis3/utils';

//used to keep track of functions used in multiple helpers/planner files
export function isOverlapping(
    startA: Date | number,
    endA: Date | number,
    startB: Date | number,
    endB: Date | number
): boolean {
    return startA <= endB && endA >= startB;
    // && (startA >= startB) && (endA <= endB);
}

export function isEquals<T>(one: T, two: T): boolean {
    if (one === two) return true;
    if (typeof one !== typeof two) return false;
    if (
        typeof one !== 'object' ||
        typeof two !== 'object' ||
        one === null ||
        two === null
    )
        return false;

    const keysOne = Object.keys(one);
    const keysTwo = Object.keys(two);

    if (keysOne.length !== keysTwo.length) return false;

    return keysOne.every((key) => {
        Object.hasOwn(two, key);
        return isEquals(one[key], two[key]);
    });
}

export function createComponent<C>(
    viewContainerRef: ViewContainerRef,
    componentType: Type<C>,
    renderer: Renderer2,
    appendTo?: any,
    inputs: { name: string; value: unknown }[] = []
): ComponentRef<C> {
    const componentRef = viewContainerRef.createComponent(componentType);
    inputs.forEach((input) => {
        componentRef.setInput(input.name, input.value);
    });
    viewContainerRef.insert(componentRef.hostView);
    if (appendTo)
        renderer.appendChild(appendTo, componentRef.location.nativeElement);
    return componentRef;
}

export function createPosition(
    event:
        | VesselSchedule
        | Outage
        | { dateTimeFrom: Date; dateTimeTo: Date; length?: number },
    rowHeight: number,
    timeline: TimelineDate[],
    xStart?: number,
    timeKeys: { time: Date | undefined; key: 'yStart' | 'yEnd' }[] = [
        { time: event.dateTimeFrom, key: 'yStart' },
        { time: event.dateTimeTo, key: 'yEnd' },
    ]
): PositionBase {
    return createPositionFactory(event, rowHeight, timeline, xStart, timeKeys);
}

export function createPositionFactory(
    event:
        | VesselSchedule
        | Outage
        | { dateTimeFrom: Date; dateTimeTo: Date; length?: number },
    rowHeight: number,
    timeline: TimelineDate[] = [],
    xStart?: number,
    timeKeys: { time: Date | undefined; key: 'yStart' | 'yEnd' }[] = [
        { time: event.dateTimeFrom, key: 'yStart' },
        { time: event.dateTimeTo, key: 'yEnd' },
    ]
): PositionBase {
    // round to nearest 5 mins instead?
    const roundUpToNearestMinutes = (date: Date, minuteToRoundTo = 5): Date => {
        const ms = 1000 * 60 * minuteToRoundTo; // 15 minutes in milliseconds
        return new Date(Math.ceil(date?.getTime() / ms) * ms);
    };

    // Initialize position with defaults based on event type
    const initializePosition = (): PositionBase => {
        if ('vesselLOA' in event) {
            return {
                id: event.id,
                xStart: xStart || event.berthStartMeter || 0,
                xEnd:
                    (xStart || event.berthStartMeter || 0) + event.vesselLOA ||
                    100,
                yStart: 0,
                yEnd: 0,
                length: Math.ceil(event.vesselLOA) || 100,
            };
        } else if ('startMeter' in event) {
            return {
                id: event.id,
                xStart: event.startMeter,
                xEnd: event.endMeter,
                yStart: 0,
                yEnd: 0,
                length: event.endMeter - event.startMeter,
            };
        } else {
            return {
                id: '',
                xStart: xStart || 0,
                xEnd: (xStart || 0) + (event.length || 0),
                yStart: 0,
                yEnd: 0,
                length: event.length || 0,
            };
        }
    };

    const pos: Position = initializePosition();

    // Calculate Y positions based on timeline;
    const calculateYPosition = () => {
        if (!timeline?.length || timeline.length < 0) return;

        const totalRows = timeline.length;
        const rowHeightPixels = rowHeight * totalRows;
        // const timelineData = timeline.data;

        timeKeys.forEach(
            (data: { time: Date | undefined; key: 'yStart' | 'yEnd' }) => {
                if (!data.time) return;

                const date = new Date(data.time);
                const key = data.key;

                // Handle edge cases
                if (key === 'yStart' && date < timeline[0].date) {
                    pos.yStart = 0;
                    return;
                }
                if (key === 'yEnd' && date > timeline[totalRows - 1].date) {
                    pos.yEnd = rowHeightPixels;
                    return;
                }

                const roundedDate = roundUpToNearestMinutes(date);
                const totalTimelineDuration =
                    (timeline[1].date.getTime() - timeline[0].date.getTime()) *
                    totalRows;
                const totalPixelHeight = rowHeightPixels;
                const pixelperms = totalPixelHeight / totalTimelineDuration;
                const eventTimeFromStart =
                    roundedDate?.getTime() - timeline[0].date?.getTime();

                pos[key] = Math.ceil(eventTimeFromStart * pixelperms);
            }
        );
    };

    calculateYPosition();
    return pos;
}
